import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircleIcon } from "@heroicons/react/24/outline";
import MarketingLayout from "../components/MarketingLayout";
import SEO from "../components/SEO";
import { apiService } from "../services/api";

const SPORTS = [
  "Hockey",
  "Soccer",
  "Basketball",
  "Baseball",
  "Softball",
  "Lacrosse",
  "Football",
  "Volleyball",
  "Other",
];

function Field({ label, required, children, hint }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1.5">
        {label}
        {required && <span className="text-sky-600 ml-0.5">*</span>}
      </label>
      {children}
      {hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
    </div>
  );
}

export default function ContactPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    club_name: "",
    sport: "",
    phone: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const set = (key) => (e) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await apiService.submitContact(form);
      setSubmitted(true);
    } catch (err) {
      setError("Something went wrong. Please try again or email us directly.");
    }
    setSubmitting(false);
  };

  const inputClass =
    "w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent placeholder-gray-400";

  return (
    <MarketingLayout>
      <SEO
        title="Contact Us"
        path="/contact"
        description="Get in touch to set up your club on Eval Ninja. We'll get you running player evaluations quickly."
      />

      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-50 via-white to-sky-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 tracking-tight">
            Get in Touch
          </h1>
          <p className="mt-6 text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto">
            Ready to run better evaluations? Tell us about your club and we'll
            get you set up.
          </p>
        </div>
      </section>

      {/* Form + side info */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            {/* Left: Info */}
            <div className="lg:col-span-2 space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">
                  What happens next?
                </h2>
                <ol className="space-y-4">
                  {[
                    "We'll review your message and reach out within 1 business day.",
                    "We'll set up your club account and walk you through the platform.",
                    "You can run your first evaluation — free.",
                  ].map((step, i) => (
                    <li key={i} className="flex gap-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-sky-100 text-sky-700 text-xs font-bold flex items-center justify-center mt-0.5">
                        {i + 1}
                      </span>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        {step}
                      </p>
                    </li>
                  ))}
                </ol>
              </div>
              {/* 
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                <EnvelopeIcon className="w-6 h-6 text-sky-600 mb-3" />
                <p className="text-sm font-semibold text-gray-900 mb-1">
                  Prefer email?
                </p>
                <p className="text-sm text-gray-600">
                  Reach us directly at{" "}
                  <a
                    href="mailto:hello@evalninja.com"
                    className="text-sky-600 hover:underline"
                  >
                    hello@evalninja.com
                  </a>
                </p>
              </div> */}

              <div>
                <p className="text-sm font-semibold text-gray-900 mb-2">
                  Already have an account?
                </p>
                <Link
                  to="/portal/login"
                  className="text-sm text-sky-600 hover:underline"
                >
                  Log in to your club portal →
                </Link>
              </div>
            </div>

            {/* Right: Form */}
            <div className="lg:col-span-3">
              {submitted ? (
                <div className="text-center py-16">
                  <CheckCircleIcon className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    Message Sent!
                  </h3>
                  <p className="text-gray-600 mb-8">
                    Thanks for reaching out. We'll be in touch within 1 business
                    day.
                  </p>
                  <Link
                    to="/"
                    className="inline-flex items-center px-6 py-3 rounded-md bg-slate-700 text-white text-sm font-medium hover:bg-slate-800 transition-colors"
                  >
                    Back to Home
                  </Link>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 space-y-6"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <Field label="Your Name" required>
                      <input
                        type="text"
                        value={form.name}
                        onChange={set("name")}
                        required
                        placeholder="Jane Smith"
                        className={inputClass}
                      />
                    </Field>
                    <Field label="Email Address" required>
                      <input
                        type="email"
                        value={form.email}
                        onChange={set("email")}
                        required
                        placeholder="jane@yourclub.com"
                        className={inputClass}
                      />
                    </Field>
                  </div>

                  <Field
                    label="Club Name"
                    required
                    hint="The name of your sports club or organization"
                  >
                    <input
                      type="text"
                      value={form.club_name}
                      onChange={set("club_name")}
                      required
                      placeholder="e.g., Riverside Hockey Club"
                      className={inputClass}
                    />
                  </Field>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <Field label="Sport">
                      <select
                        value={form.sport}
                        onChange={set("sport")}
                        className={inputClass}
                      >
                        <option value="">Select a sport...</option>
                        {SPORTS.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Phone Number">
                      <input
                        type="tel"
                        value={form.phone}
                        onChange={set("phone")}
                        placeholder="(555) 000-0000"
                        className={inputClass}
                      />
                    </Field>
                  </div>

                  <Field
                    label="Message"
                    required
                    hint="Tell us about your club — how many players, what you're evaluating, any questions"
                  >
                    <textarea
                      value={form.message}
                      onChange={set("message")}
                      required
                      rows={5}
                      placeholder="We run tryouts for 100+ players each spring and need a better way to organize scoring across 6 evaluators..."
                      className={inputClass}
                    />
                  </Field>

                  {error && (
                    <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                      {error}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-3 px-6 rounded-lg bg-slate-700 hover:bg-slate-800 text-white text-sm font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? "Sending..." : "Send Message"}
                  </button>

                  <p className="text-xs text-gray-400 text-center">
                    By submitting this form you agree to our{" "}
                    <Link to="/privacy" className="hover:underline">
                      Privacy Policy
                    </Link>
                    .
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
